/**
 * BulkEditDialog - Edit multiple suggestions at once
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { EntrySuggestion, Library, CustomPatternBuilder, FieldMappingOverride } from '@/types/api';
import CustomPatternBuilderComponent, { PatternBuilderElement } from './CustomPatternBuilder';
import { useReparseSuggestions } from '@/hooks/useSuggestions';
import { useCreateLibrary } from '@/hooks/useLibraries';
import { useCreateLibraryAlias } from '@/hooks/useLibraryAliases';
import { LibraryAliasType } from '@/types/enums';
import CreateLibraryDialog from '@/components/indexChannel/CreateLibraryDialog';

export interface BulkEditValues {
  libraryId?: number;
  author?: string;
  reparse: boolean;
  pattern?: string;
  customPattern?: CustomPatternBuilder;
  fieldMapping?: FieldMappingOverride;
}

interface BulkEditDialogProps {
  open: boolean;
  selectedCount: number;
  selectedMessageIds: number[];
  chatId: number | null;
  libraries: Library[];
  onRefreshLibraries?: () => Promise<unknown> | void;
  onClose: () => void;
  onApply: (values: BulkEditValues) => void;
}

const PATTERN_OPTIONS = [
  { value: 'separator_title_media' },
  { value: 'title_album' },
  { value: 'title_media_burst' },
  { value: 'title_single_media' },
];
const CREATE_LIBRARY_OPTION = '__create_new_library__';

const BulkEditDialog: React.FC<BulkEditDialogProps> = ({
  open,
  selectedCount,
  selectedMessageIds,
  chatId,
  libraries,
  onRefreshLibraries,
  onClose,
  onApply,
}) => {
  const { t } = useTranslation('components');
  const [libraryId, setLibraryId] = useState<number | ''>('');
  const [author, setAuthor] = useState('');
  const [useReparse, setUseReparse] = useState(false);
  const [pattern, setPattern] = useState(PATTERN_OPTIONS[0].value);
  const [useCustomPattern, setUseCustomPattern] = useState(false);
  const [customPatternElements, setCustomPatternElements] = useState<PatternBuilderElement[]>([]);
  const [customPatternErrors, setCustomPatternErrors] = useState<string[]>([]);
  const [useFieldMapping, setUseFieldMapping] = useState(false);
  const [fieldMapping, setFieldMapping] = useState<FieldMappingOverride>({
    title: 'title',
    author: 'author',
    ms_no: 'ms_no',
  });
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewSuggestion, setPreviewSuggestion] = useState<EntrySuggestion | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [createLibraryOpen, setCreateLibraryOpen] = useState(false);

  const { mutateAsync: reparseSuggestions } = useReparseSuggestions();
  const createLibraryMutation = useCreateLibrary();
  const createAliasMutation = useCreateLibraryAlias();

  useEffect(() => {
    if (!open) return;
    setLibraryId('');
    setAuthor('');
    setUseReparse(false);
    setPattern(PATTERN_OPTIONS[0].value);
    setUseCustomPattern(false);
    setCustomPatternElements([]);
    setCustomPatternErrors([]);
    setUseFieldMapping(false);
    setFieldMapping({
      title: 'title',
      author: 'author',
      ms_no: 'ms_no',
    });
    setPreviewIndex(0);
    setPreviewSuggestion(null);
    setPreviewError(null);
  }, [open]);

  useEffect(() => {
    if (!useCustomPattern) {
      setUseFieldMapping(false);
      setCustomPatternErrors([]);
    }
  }, [useCustomPattern]);

  // Helper function to translate pattern elements
  const translatePattern = (pattern: string): string => {
    // Pattern format: "ELEMENT1_ELEMENT2_ELEMENT3" e.g., "separator_title_media"
    // Convert to uppercase for translation lookup
    const parts = pattern.toUpperCase().split('_');
    const translatedParts = parts.map((part) => {
      // Try to translate individual element type, fallback to uppercase
      return t(`customPatternBuilder.patternElementTypes.${part}`, part);
    });
    return translatedParts.join(' → ');
  };

  const handleCustomPatternChange = (elements: PatternBuilderElement[]) => {
    setCustomPatternElements(elements);
    setCustomPatternErrors([]);
  };

  useEffect(() => {
    if (previewIndex >= selectedMessageIds.length) {
      setPreviewIndex(0);
    }
  }, [previewIndex, selectedMessageIds]);

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      if (!open || !useReparse || !chatId || selectedMessageIds.length === 0) {
        setPreviewSuggestion(null);
        setPreviewError(null);
        return;
      }

      const targetId = selectedMessageIds[Math.min(previewIndex, selectedMessageIds.length - 1)];
      if (!targetId) {
        setPreviewSuggestion(null);
        setPreviewError(null);
        return;
      }

      setPreviewLoading(true);
      setPreviewError(null);

      try {
        const response = await reparseSuggestions({
          chat_id: chatId,
          message_ids: [targetId],
          pattern: useCustomPattern ? undefined : pattern,
          custom_pattern: useCustomPattern
            ? { elements: customPatternElements.map(({ type, maps_to }) => ({ type, maps_to })) }
            : undefined,
          field_mapping: useCustomPattern && useFieldMapping ? fieldMapping : undefined,
        });

        if (cancelled) return;

        const suggestion = response.suggestions.find(
          (item) => item.title_message_id === targetId
        );
        setPreviewSuggestion(suggestion || null);
        setPreviewError(suggestion ? null : t('bulkEditDialog.preview.noSuggestion'));
      } catch (error) {
        if (!cancelled) {
          setPreviewSuggestion(null);
          setPreviewError(t('bulkEditDialog.preview.error'));
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    useReparse,
    chatId,
    selectedMessageIds,
    previewIndex,
    pattern,
    useCustomPattern,
    customPatternElements,
    useFieldMapping,
    fieldMapping,
    reparseSuggestions,
  ]);

  const handleApply = () => {
    const errors: string[] = [];
    if (useReparse && useCustomPattern) {
      if (customPatternElements.length === 0) {
        errors.push(t('bulkEditDialog.errors.customPatternEmpty'));
      }

      const hasMedia = customPatternElements.some((element) => element.maps_to === 'media');
      if (!hasMedia) {
        errors.push(t('bulkEditDialog.errors.customPatternNoMedia'));
      }

      const hasTitleOrMs = customPatternElements.some(
        (element) => element.maps_to === 'title' || element.maps_to === 'ms_no'
      );
      if (!hasTitleOrMs) {
        errors.push(t('bulkEditDialog.errors.customPatternNoTitleOrMs'));
      }
    }

    if (errors.length > 0) {
      setCustomPatternErrors(errors);
      return;
    }

    setCustomPatternErrors([]);

    onApply({
      libraryId: libraryId === '' ? undefined : libraryId,
      author: author.trim() === '' ? undefined : author.trim(),
      reparse: useReparse,
      pattern: useReparse && !useCustomPattern ? pattern : undefined,
      customPattern:
        useReparse && useCustomPattern
          ? { elements: customPatternElements.map(({ type, maps_to }) => ({ type, maps_to })) }
          : undefined,
      fieldMapping: useReparse && useCustomPattern && useFieldMapping ? fieldMapping : undefined,
    });
  };

  const handleCreateLibrarySubmit = async (data: {
    canonical_name: string;
    slug: string;
    notes: string;
    aliases: string[];
  }) => {
    try {
      const newLibrary = await createLibraryMutation.mutateAsync({
        canonical_name: data.canonical_name,
        slug: data.slug || data.canonical_name.toLowerCase().replace(/\s+/g, '-'),
        notes: data.notes || undefined,
      });

      if (data.aliases.length > 0) {
        for (const aliasText of data.aliases) {
          try {
            await createAliasMutation.mutateAsync({
              libraryId: newLibrary.id,
              data: {
                alias: aliasText,
                alias_type: aliasText.startsWith('#')
                  ? LibraryAliasType.HASHTAG
                  : LibraryAliasType.TEXT,
              },
            });
          } catch (error) {
            console.error(`Failed to create alias "${aliasText}":`, error);
          }
        }
      }

      await onRefreshLibraries?.();
      setLibraryId(newLibrary.id);
      setCreateLibraryOpen(false);
    } catch (error) {
      console.error('Failed to create library:', error);
      alert(t('bulkEditDialog.createLibrary.failed', 'Failed to create library. Please try again.'));
    }
  };

  const handleLibraryChange = (value: string | number) => {
    if (value === CREATE_LIBRARY_OPTION) {
      setCreateLibraryOpen(true);
      return;
    }
    if (value === '') {
      setLibraryId('');
      return;
    }
    setLibraryId(Number(value));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('bulkEditDialog.title', { count: selectedCount })}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('bulkEditDialog.manualOverrides')}
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('bulkEditDialog.libraryLabel')}</InputLabel>
              <Select
                value={libraryId}
                label={t('bulkEditDialog.libraryLabel')}
                onChange={(event) => handleLibraryChange(event.target.value as string | number)}
              >
                <MenuItem value="">
                  <em>{t('bulkEditDialog.noChange')}</em>
                </MenuItem>
                {libraries.map((library) => (
                  <MenuItem key={library.id} value={library.id}>
                    {library.canonical_name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value={CREATE_LIBRARY_OPTION}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon fontSize="small" />
                    {t('bulkEditDialog.createLibrary.option', 'Create New Library')}
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={t('bulkEditDialog.authorLabel')}
              fullWidth
              margin="normal"
              value={author}
              onChange={(event) => setAuthor(event.target.value)}
              helperText={t('bulkEditDialog.authorHelper')}
              inputProps={{ dir: 'rtl' }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useReparse}
                  onChange={(event) => setUseReparse(event.target.checked)}
                />
              }
              label={t('bulkEditDialog.reparse.label')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={useCustomPattern}
                  onChange={(event) => setUseCustomPattern(event.target.checked)}
                  disabled={!useReparse}
                />
              }
              label={t('bulkEditDialog.reparse.customPatternLabel')}
            />

          {!useCustomPattern && (
            <FormControl fullWidth margin="normal" disabled={!useReparse}>
              <InputLabel>{t('bulkEditDialog.reparse.patternLabel')}</InputLabel>
              <Select value={pattern} label={t('bulkEditDialog.reparse.patternLabel')} onChange={(event) => setPattern(event.target.value)}>
                {PATTERN_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(`customPatternBuilder.patternOptions.${option.value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {useCustomPattern && (
            <Box sx={{ mt: 2 }}>
              <CustomPatternBuilderComponent
                elements={customPatternElements}
                onChange={handleCustomPatternChange}
                errors={customPatternErrors}
              />
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={useFieldMapping}
                onChange={(event) => setUseFieldMapping(event.target.checked)}
                disabled={!useReparse || !useCustomPattern}
              />
            }
            label={t('bulkEditDialog.reparse.overrideMappingLabel')}
          />

          {useFieldMapping && useCustomPattern && (
            <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('bulkEditDialog.reparse.titleMapsTo')}</InputLabel>
                <Select
                  value={fieldMapping.title || 'title'}
                  label={t('bulkEditDialog.reparse.titleMapsTo')}
                  onChange={(event) =>
                    setFieldMapping((prev) => ({ ...prev, title: event.target.value as FieldMappingOverride['title'] }))
                  }
                >
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="author">Author</MenuItem>
                  <MenuItem value="ms_no">MS No</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>{t('bulkEditDialog.reparse.authorMapsTo')}</InputLabel>
                <Select
                  value={fieldMapping.author || 'author'}
                  label={t('bulkEditDialog.reparse.authorMapsTo')}
                  onChange={(event) =>
                    setFieldMapping((prev) => ({ ...prev, author: event.target.value as FieldMappingOverride['author'] }))
                  }
                >
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="author">Author</MenuItem>
                  <MenuItem value="ms_no">MS No</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>{t('bulkEditDialog.reparse.msNoMapsTo')}</InputLabel>
                <Select
                  value={fieldMapping.ms_no || 'ms_no'}
                  label={t('bulkEditDialog.reparse.msNoMapsTo')}
                  onChange={(event) =>
                    setFieldMapping((prev) => ({ ...prev, ms_no: event.target.value as FieldMappingOverride['ms_no'] }))
                  }
                >
                  <MenuItem value="title">Title</MenuItem>
                  <MenuItem value="author">Author</MenuItem>
                  <MenuItem value="ms_no">MS No</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('bulkEditDialog.reparse.helperText')}
          </Typography>
        </Box>

          {useReparse && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                {t('bulkEditDialog.preview.title')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setPreviewIndex((prev) => Math.max(prev - 1, 0))}
                  disabled={previewIndex === 0}
                >
                  {t('bulkEditDialog.preview.previous')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setPreviewIndex((prev) =>
                      Math.min(prev + 1, Math.max(selectedMessageIds.length - 1, 0))
                    )
                  }
                  disabled={previewIndex >= selectedMessageIds.length - 1}
                >
                  {t('bulkEditDialog.preview.next')}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {t('bulkEditDialog.preview.exampleOf', {
                    current: selectedMessageIds.length === 0 ? 0 : previewIndex + 1,
                    total: selectedMessageIds.length,
                    messageId: selectedMessageIds[previewIndex] || '—',
                  })}
                </Typography>
              </Box>

              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {previewLoading && (
                  <Typography variant="body2" color="text.secondary">
                    {t('bulkEditDialog.preview.loading')}
                  </Typography>
                )}
                {!previewLoading && previewError && (
                  <Typography variant="body2" color="error">
                    {previewError}
                  </Typography>
                )}
                {!previewLoading && previewSuggestion && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('bulkEditDialog.preview.pattern', { pattern: translatePattern(previewSuggestion.pattern) })}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 1, direction: 'rtl', textAlign: 'right' }}>
                      {previewSuggestion.title || t('bulkEditDialog.preview.noTitle')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                      <Typography variant="body2">
                        {t('bulkEditDialog.preview.author', { author: previewSuggestion.author || '—' })}
                      </Typography>
                      <Typography variant="body2">
                        {t('bulkEditDialog.preview.msNo', { msNo: previewSuggestion.ms_no || '—' })}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t('bulkEditDialog.preview.mediaCount', { count: previewSuggestion.media_ids.length })}
                    </Typography>
                    {previewSuggestion.library_matches.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('bulkEditDialog.preview.library', { library: previewSuggestion.library_matches[0].library_name })}
                      </Typography>
                    )}
                    {previewSuggestion.reasons.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('bulkEditDialog.preview.reasons')}
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {previewSuggestion.reasons.slice(0, 3).map((reason, index) => (
                            <Typography component="li" variant="caption" key={index} color="text.secondary">
                              {reason}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('bulkEditDialog.actions.cancel')}</Button>
          <Button variant="contained" onClick={handleApply}>
            {t('bulkEditDialog.actions.apply')}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateLibraryDialog
        open={createLibraryOpen}
        initialName=""
        isLoading={createLibraryMutation.isPending || createAliasMutation.isPending}
        onClose={() => setCreateLibraryOpen(false)}
        onSubmit={handleCreateLibrarySubmit}
      />
    </>
  );
};

export default BulkEditDialog;
